import { isReviewPreview, previewReviewUrl, readReviewResponse } from './ag-review-preview.js';
import { reviewCountryFlag } from "@astrography/review-country";
// The homepage follows the merchant selection; product pages retain product-first order.
export function selectCompactGallery(productReviews, featured, productId) {
  const selected = featured.filter(review => review.rating >= 4 && review.media?.some(media => media.kind === 'image'));
  const seen = new Set();
  const ordered = productId ? [...productReviews, ...selected] : [...selected, ...productReviews];
  return ordered.filter(review => review.rating >= 4 && !seen.has(review.id) && seen.add(review.id));
}
/* Public review widget. Customer content is inserted as text, never executable HTML. */
if (!customElements.get('ag-customer-reviews')) {
  customElements.define('ag-customer-reviews', class extends HTMLElement {
    #abort; #page = 1; #renderedPage = 1; #total = 0; #timer; #events; #measurement; #voter; #voting = false; #voted = new Set(); #overviewReviews = new Map(); #opener; #scrollOverflow; #railObserver; #railRefresh;
    connectedCallback() {
      if (this.#events) return;
      this.#events = new AbortController();
      const options = { signal: this.#events.signal };
      if (this.dataset.api && this.dataset.layout === 'compact') void this.#loadMedia(options.signal);
      this.#total = Number(this.dataset.total || 0);
      this.querySelectorAll('[data-ag-cr-country]').forEach(node => this.#country(node, node.textContent));
      this.querySelectorAll('[data-ag-cr-review-id]').forEach(article => this.#actions(article));
      if (this.dataset.layout === 'compact') { this.#connectRail(options); if (this.dataset.api) void this.#loadCompact(options.signal); }
      const form = this.querySelector('[data-ag-cr-filters]');
      const requestedRating = new URL(location.href).searchParams.get('rating');
      if (form && /^[0-5]$/.test(requestedRating || '')) form.elements.rating.value = requestedRating;
      form?.addEventListener('submit', event => { event.preventDefault(); this.#page = 1; void this.#load(); }, options);
      form?.addEventListener('change', () => { this.#page = 1; void this.#load(); }, options);
      form?.querySelector('input')?.addEventListener('input', () => { clearTimeout(this.#timer); this.#timer = setTimeout(() => { this.#page = 1; void this.#load(); }, 350); }, options);
      this.querySelector('[data-ag-cr-prev]')?.addEventListener('click', () => { this.#page--; void this.#load(); }, options);
      this.querySelector('[data-ag-cr-next]')?.addEventListener('click', () => { this.#page++; void this.#load(); }, options);
      this.addEventListener('click', event => {
        const helpful = event.target.closest('[data-ag-cr-helpful]');
        if (helpful) void this.#vote(helpful);
        const share = event.target.closest('[data-ag-cr-share]');
        if (share) void this.#share(share);
        if (event.target.closest('[data-ag-cr-show-media]')) { form.elements.media.value = 'with_media'; this.#page = 1; void this.#load(); form.scrollIntoView({block:'start',behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'}); }
        const rating = event.target.closest('[data-ag-cr-rating]');
        if (rating) { form.elements.rating.value = form.elements.rating.value === rating.dataset.agCrRating ? '0' : rating.dataset.agCrRating; this.#page = 1; void this.#load(); }
        const open = event.target.closest('[data-ag-cr-open-review],[data-ag-cr-permalink],[data-ag-cr-photo]');
        if (open && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0 && this.#openReview(open)) event.preventDefault();
        const thumbnail = event.target.closest('[data-ag-cr-detail-media]');
        if (thumbnail) this.#selectDetailMedia(thumbnail);
        if (event.target.closest('[data-ag-cr-close]')) this.querySelector('dialog').close();
      }, options);
      const dialog = this.querySelector('dialog');
      dialog?.addEventListener('click', event => { if (event.target === dialog) { const box = dialog.getBoundingClientRect(); if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close(); } }, options);
      dialog?.addEventListener('close', () => this.#closeReview(), options);
      if (this.dataset.api && form) void this.#load();
      if (isReviewPreview(this) && form) {
        const id = new URL(location.href).searchParams.get('review');
        if (/^[a-f0-9-]{36}$/i.test(id || '')) {
          readReviewResponse(this,`${this.dataset.api}/projection`,options).then(response=>response.json()).then(data=>{
            const review = data.previewRecords?.find(record=>record.id === id);
            if (review && !options.signal.aborted) this.#openReview(this,this.#review(review));
          }).catch(()=>{});
        }
      }
      if (!this.dataset.api) { form?.querySelectorAll('input,select,button').forEach(node => { node.disabled = true; }); }
      if (this.dataset.api && this.dataset.measurement === 'true' && this.dataset.measurementScript) {
        const signal = this.#events.signal;
        import(this.dataset.measurementScript).then(({ ReviewMeasurement }) => {
          if (!signal.aborted) this.#measurement = new ReviewMeasurement(this);
        }).catch(() => {});
      }
    }
    disconnectedCallback() { this.querySelector('dialog')?.close(); this.#closeReview(); this.#overviewReviews.clear(); this.#abort?.abort(); this.#events?.abort(); this.#events = null; this.#measurement?.disconnect(); this.#railObserver?.disconnect(); clearTimeout(this.#timer); }
    #connectRail(options) {
      const rail = this.querySelector('.ag-cr__rail'), controls = this.querySelector('[data-ag-cr-rail-controls]');
      if (!rail || !controls) return;
      const previous = controls.querySelector('[data-ag-cr-rail-prev]'), next = controls.querySelector('[data-ag-cr-rail-next]');
      const update = () => {
        const overflow = rail.scrollWidth - rail.clientWidth;
        controls.hidden = overflow < 2;
        rail.style.setProperty('--ag-cr-fade-start', rail.scrollLeft > 2 ? '48px' : '0px');
        rail.style.setProperty('--ag-cr-fade-end', rail.scrollLeft < overflow - 2 ? '56px' : '0px');
        previous.disabled = rail.scrollLeft < 2;
        next.disabled = rail.scrollLeft >= overflow - 2;
      };
      this.#railRefresh = update;
      const move = direction => {
        const card = rail.querySelector('article');
        const step = (card?.getBoundingClientRect().width || rail.clientWidth) + (parseFloat(getComputedStyle(rail).columnGap) || 0);
        rail.scrollBy({left: direction * step, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      };
      previous.addEventListener('click', () => move(-1), options);
      next.addEventListener('click', () => move(1), options);
      rail.addEventListener('scroll', update, {...options, passive:true});
      this.#railObserver = new ResizeObserver(update); this.#railObserver.observe(rail); update();
    }
    #stopVideo() { this.querySelectorAll('dialog video').forEach(video => { video.pause(); video.removeAttribute('src'); video.load(); }); }
    #closeReview() {
      this.#stopVideo();
      if (this.#scrollOverflow !== undefined) { document.documentElement.style.overflow = this.#scrollOverflow; this.#scrollOverflow = undefined; }
      this.querySelector('[data-ag-cr-detail-content]')?.replaceChildren();
      if (this.#opener?.isConnected) this.#opener.focus({preventScroll:true});
      this.#opener = null;
    }
    // Reuse the public review dialog for the hero's country stories without changing filters.
    openPublicReview(review, trigger) {
      if (!review || !this.#permalink(review.id)) return false;
      return this.#openReview(trigger, this.#review(review));
    }
    #openReview(trigger, suppliedArticle) {
      if (trigger.closest('dialog')) return false;
      const original = suppliedArticle || trigger.closest('[data-ag-cr-review-id]') || this.#overviewReviews.get(trigger.dataset.agCrOpenReview);
      const dialog = this.querySelector('dialog'), host = dialog?.querySelector('[data-ag-cr-detail-content]');
      if (!original || !host) return false;
      const article = original.cloneNode(true), photos = [...article.querySelectorAll('.ag-cr__photos a')];
      const selected = Math.max(0,[...original.querySelectorAll('.ag-cr__photos a')].findIndex(photo => photo.href === trigger.href));
      article.classList.add('ag-cr__detail-review');
      article.querySelectorAll('.ag-cr__preview,.ag-cr__photos,[data-ag-cr-helpful],[data-ag-cr-permalink]').forEach(node => node.remove());
      const heading = article.querySelector('h3');
      if (!heading) return false;
      if (!heading.textContent.trim()) heading.textContent = 'Customer experience';
      heading.id = `ag-cr-detail-${crypto.randomUUID()}`; heading.tabIndex = -1; heading.setAttribute('autofocus',''); heading.dataset.agCrInitialFocus = '';
      dialog.setAttribute('aria-labelledby',heading.id);
      const actions = article.querySelector('.ag-cr__actions');
      const permalink = this.#permalink(article.dataset.agCrReviewId);
      if (actions && permalink && !isReviewPreview(this)) { const link = this.#node('a','','Open full page'); link.href = permalink; link.target = '_blank'; link.rel = 'noopener noreferrer'; actions.append(link); }
      const layout = this.#node('div','ag-cr__detail-layout');
      if (photos.length) {
        const media = this.#node('div','ag-cr__detail-media'), stage = this.#node('div','ag-cr__detail-stage'); stage.dataset.agCrDetailStage = '';
        const thumbs = this.#node('div','ag-cr__detail-thumbs'); thumbs.setAttribute('aria-label','Review attachments');
        photos.forEach((photo,index) => {
          const button = this.#node('button'); button.type = 'button'; button.dataset.agCrDetailMedia = photo.href; button.dataset.agCrMediaKind = photo.dataset.agCrMediaKind || 'image'; button.dataset.agCrPoster = photo.dataset.agCrPoster || '';
          button.setAttribute('aria-label',`Show ${button.dataset.agCrMediaKind === 'video' ? 'video' : 'photo'} ${index+1} of ${photos.length}`);
          const img = photo.querySelector('img')?.cloneNode(true); if (img) { button.dataset.agCrSrcset = img.srcset; img.sizes = '64px'; img.loading = 'eager'; img.alt = ''; button.append(img); }
          if (button.dataset.agCrMediaKind === 'video') button.append(this.#node('span','ag-cr__video-badge','▶'));
          thumbs.append(button);
        });
        thumbs.hidden = photos.length === 1; media.append(stage,thumbs);
        layout.append(media); layout.classList.add('ag-cr__detail-layout--media');
      }
      layout.append(article); host.replaceChildren(layout);
      if (photos.length) this.#selectDetailMedia(host.querySelectorAll('[data-ag-cr-detail-media]')[selected]);
      this.#opener = trigger; this.#scrollOverflow = document.documentElement.style.overflow; document.documentElement.style.overflow = 'hidden';
      dialog.showModal(); heading.focus({preventScroll:true});
      return true;
    }
    #selectDetailMedia(button) {
      const stage = this.querySelector('[data-ag-cr-detail-stage]'); if (!stage || !button) return;
      this.#stopVideo();
      const isVideo = button.dataset.agCrMediaKind === 'video';
      const asset = this.#node(isVideo ? 'video' : 'img');
      if (isVideo) { asset.controls = true; asset.playsInline = true; asset.preload = 'metadata'; if (button.dataset.agCrPoster) asset.poster = button.dataset.agCrPoster; asset.setAttribute('aria-label','Customer video'); }
      else { asset.alt = button.getAttribute('aria-label').replace('Show ','Customer '); asset.width = 1200; asset.height = 1000; if (button.dataset.agCrSrcset) { asset.srcset = button.dataset.agCrSrcset; asset.sizes = '(max-width: 760px) calc(100vw - 56px), 600px'; } }
      asset.src = button.dataset.agCrDetailMedia;
      const status = this.#node('p','ag-cr__detail-media-status','Loading attachment…'); status.setAttribute('role','status');
      asset.addEventListener(isVideo ? 'loadedmetadata' : 'load',()=>{status.textContent = '';},{once:true});
      asset.addEventListener('error',()=>{status.textContent = 'This attachment could not be loaded. Try another photo or open the full page.';},{once:true});
      stage.replaceChildren(asset,status);
    }
    #node(tag, className, text) { const node = document.createElement(tag); if (className) node.className = className; if (text !== undefined) node.textContent = String(text); return node; }
    #permalink(id) {
      if (!/^[a-f0-9-]{36}$/i.test(id || '')) return '';
      if (isReviewPreview(this)) return previewReviewUrl(id);
      try {
        const form = new URL(this.dataset.form, location.origin);
        const shop = form.pathname.match(/^\/review\/([a-f0-9-]{36})\/?$/i)?.[1];
        if (!shop || form.username || form.password || !(form.protocol === 'https:' || (form.protocol === 'http:' && ['localhost','127.0.0.1'].includes(form.hostname)))) return '';
        return `${form.origin}/api/reviews/${shop}/permalink/${id}`;
      } catch { return ''; }
    }
    #actions(article) {
      const url = this.#permalink(article.dataset.agCrReviewId);
      if (!url) return;
      if (isReviewPreview(this)) {
        article.querySelector('.ag-cr__actions')?.replaceChildren();
        if (this.dataset.layout === 'compact') this.#compactPreview(article,url);
        return;
      }
      let actions = article.querySelector('.ag-cr__actions');
      if (!actions) { actions = this.#node('div','ag-cr__actions'); article.lastElementChild.append(actions); }
      let link = actions.querySelector('[data-ag-cr-permalink]');
      if (!link) { link = this.#node('a','','Open review'); link.dataset.agCrPermalink = ''; actions.append(link); }
      link.href = url; link.target = '_blank'; link.rel = 'noopener noreferrer';
      if (this.dataset.layout === 'compact') this.#compactPreview(article, url);
      if (!actions.querySelector('[data-ag-cr-share]')) {
        const button = this.#node('button','','Share review'); button.type = 'button'; button.dataset.agCrShare = ''; actions.prepend(button);
        const status = this.#node('span','ag-cr__share-status'); status.setAttribute('role','status'); status.dataset.agCrShareStatus = ''; actions.append(status);
      }
      if (this.dataset.api && !actions.querySelector('[data-ag-cr-helpful]')) {
        const button = this.#node('button','',this.#voted.has(article.dataset.agCrReviewId) ? 'Thanks for your feedback' : 'Helpful');
        button.type = 'button'; button.dataset.agCrHelpful = ''; button.disabled = this.#voting || this.#voted.has(article.dataset.agCrReviewId); actions.prepend(button);
      }
    }
    #compactPreview(article, url) {
      let preview = article.querySelector('.ag-cr__preview');
      if (!preview) {
        preview = this.#node('div','ag-cr__preview');
        const top = this.#node('div','ag-cr__preview-top');
        top.append(article.querySelector('.ag-cr__stars').cloneNode(true));
        const verified = article.querySelector('.ag-cr__verified');
        if (verified) top.append(verified.cloneNode(true));
        const heading = this.#node('h3');
        const title = article.querySelector('h3').textContent || 'Customer experience';
        const author = article.querySelector('.ag-cr__author strong').textContent;
        const link = this.#node('a','',title); link.dataset.agCrOpenReview = '';
        link.setAttribute('aria-label',`Read review: ${title} — ${author}`); heading.append(link);
        const body = this.#node('p','ag-cr__preview-body',article.querySelector('.ag-cr__body').textContent);
        const footer = this.#node('p','ag-cr__preview-footer'); footer.append(this.#node('span','',author));
        const country = article.querySelector('.ag-cr__author [data-ag-cr-country]');
        if (country) footer.append(document.createTextNode(' · '),country.cloneNode(true));
        footer.append(this.#node('br'), this.#node('span','',article.querySelector('.ag-cr__product').textContent.replace(/^Product reviewed: /,'')));
        preview.append(top,heading,body,footer); article.append(preview);
      }
      preview.querySelector('[data-ag-cr-open-review]').href = url;
    }
    async #vote(button) {
      if (isReviewPreview(this)) return;
      const article = button.closest('[data-ag-cr-review-id]'), id = article?.dataset.agCrReviewId;
      const shop = this.dataset.api?.match(/\/api\/reviews\/([a-f0-9-]{36})\/?$/i)?.[1];
      if (!shop || !id || this.#voting || this.#voted.has(id)) return;
      this.#voting = true; this.querySelectorAll('[data-ag-cr-helpful]').forEach(node => { node.disabled = true; });
      const key = `astro_reviews_helpful:${shop}`, status = article.querySelector('[data-ag-cr-share-status]'); status.textContent = '';
      try {
        // Access this functional preference only after an explicit Helpful action.
        if (!this.#voter) {
          let saved;
          try { saved = JSON.parse(localStorage.getItem(key) || 'null'); } catch {}
          this.#voter = { requestId: /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(saved?.requestId || '') ? saved.requestId : crypto.randomUUID() };
          if (typeof saved?.ticket === 'string' && /^v1\.[a-f0-9-]{36}\.[a-zA-Z0-9_-]{43}$/.test(saved.ticket)) this.#voter.ticket = saved.ticket;
        }
        try { localStorage.setItem(key, JSON.stringify(this.#voter)); } catch {}
        const response = await readReviewResponse(this, `${this.dataset.api.replace(/\/$/,'')}/records/${id}/helpful`, { method:'POST', headers:{'content-type':'application/json'}, body:JSON.stringify(this.#voter), signal:AbortSignal.timeout(15000) });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || 'Your feedback could not be saved. Please try again.');
        if (!Number.isInteger(result.helpful) || result.helpful < 0 || result.voted !== true || typeof result.ticket !== 'string' || !/^v1\.[a-f0-9-]{36}\.[a-zA-Z0-9_-]{43}$/.test(result.ticket)) throw new Error('Your feedback could not be confirmed. Please try again.');
        this.#voter.ticket = result.ticket;
        try { localStorage.setItem(key, JSON.stringify(this.#voter)); } catch {}
        this.#voted.add(id);
        const current = this.querySelector(`[data-ag-cr-review-id="${CSS.escape(id)}"]`);
        if (current) {
          const count = current.querySelector('[data-ag-cr-helpful-count]'); if (count) count.textContent = result.helpful;
          current.querySelector('[data-ag-cr-helpful]').textContent = 'Thanks for your feedback';
          current.querySelector('[data-ag-cr-share-status]').textContent = 'Your feedback helps other customers.';
        }
      } catch (error) {
        if (error.message?.includes('voting preference')) { this.#voter = null; try { localStorage.removeItem(key); } catch {} status.textContent = 'Your saved voting preference changed. Please try again.'; }
        else status.textContent = 'Your feedback could not be saved. Please try again.';
      } finally {
        this.#voting = false;
        this.querySelectorAll('[data-ag-cr-helpful]').forEach(node => { node.disabled = this.#voted.has(node.closest('[data-ag-cr-review-id]').dataset.agCrReviewId); });
      }
    }
    async #share(button) {
      const article = button.closest('[data-ag-cr-review-id]'), url = this.#permalink(article?.dataset.agCrReviewId);
      if (!url || button.disabled) return;
      const status = article.querySelector('[data-ag-cr-share-status]'); status.textContent = ''; button.disabled = true;
      try {
        if (typeof navigator.share === 'function') {
          await navigator.share({ title: article.querySelector('h3')?.textContent || 'Customer review', url });
          status.textContent = 'Review shared.';
        } else { await navigator.clipboard.writeText(url); status.textContent = 'Public review link copied.'; }
      } catch (error) { if (error.name !== 'AbortError') status.textContent = 'Open this review to copy its address.'; }
      finally { button.disabled = false; }
    }
    #videoUrl(value) { if (typeof value !== 'string' || !value) return ''; try { const origin = new URL(this.dataset.form, location.origin).origin, url = new URL(value, origin); return !url.username && !url.password && (url.protocol === 'https:' || (url.protocol === 'http:' && ['localhost','127.0.0.1'].includes(url.hostname))) ? url.href : ''; } catch { return ''; } }
    #imageUrl(value) {
      try {
        const base = new URL(this.dataset.api, location.origin);
        const prefix = base.pathname.split('/api/reviews/')[0];
        const url = new URL(value.startsWith('/api/reviews/') ? prefix + value : value, base.origin);
        return url.protocol === 'https:' || (url.protocol === 'http:' && ['localhost','127.0.0.1'].includes(url.hostname)) ? url.href : '';
      } catch { return ''; }
    }
    #country(node, country) {
      node.textContent = '';
      const flag = reviewCountryFlag(country);
      if (flag) { const icon = this.#node('span', 'ag-cr__flag', flag); icon.setAttribute('aria-hidden', 'true'); node.append(icon, ' '); }
      node.append(document.createTextNode(country));
    }
    #context(review) {
      return review.product?.id === this.dataset.product ? 'This product' : review.product ? 'Another product' : 'Store experience';
    }
    #review(review) {
      const article = this.#node('article','ag-cr__review');
      article.dataset.agCrReviewId = review.id;
      const author = this.#node('div','ag-cr__author'); author.append(this.#node('strong','',review.authorName));
      if (review.shopSyndicated) {
        const badge=this.#node('img','ag-cr__shop-badge');
        badge.src='https://cdn.shopify.com/static/shop/Verified%20by%20Shop_Black_EN.svg'; badge.alt='Verified by Shop'; badge.width=114; badge.height=24; badge.loading='lazy'; author.append(badge);
      }
      const badges = { purchase:'Verified purchase', source:'Verified by source', shop:'Verified by Shop', email:'Email confirmed' };
      if (badges[review.verification]) author.append(this.#node('span','ag-cr__verified',badges[review.verification]));
      if (review.country) { const country = this.#node('span'); country.dataset.agCrCountry = ''; this.#country(country, review.country); author.append(country); }
      author.append(this.#node('time','',new Date(review.submittedAt).toLocaleDateString('en',{month:'short',day:'numeric',year:'numeric'})));
      const content = this.#node('div'); const stars = this.#node('span','ag-cr__stars','★★★★★');
      stars.style.setProperty('--ag-cr-rating', Math.max(0,Math.min(5,Number(review.rating))) * 20 + '%'); stars.setAttribute('role','img'); stars.setAttribute('aria-label',`${review.rating} out of 5 stars`);
      content.append(stars,this.#node('h3','',review.title || 'Customer experience'),this.#node('p','ag-cr__body',review.body || 'This customer left a rating.'),this.#node('p','ag-cr__product',review.product ? `Product reviewed: ${review.product.name}` : 'Store experience'));
      if (this.dataset.layout === 'compact' && this.dataset.product) stars.after(this.#node('span','ag-cr__context',this.#context(review)));
      for (const translation of Array.isArray(review.translations) ? review.translations : []) {
        const details = this.#node('details','ag-cr__translation');
        details.append(this.#node('summary','',`Read in ${translation.languageName || translation.locale}`),this.#node('p','ag-cr__source','Translation provided by the store. The original review is shown above.'));
        const translated = this.#node('div'); translated.lang = translation.locale; translated.dir = 'auto';
        translated.append(this.#node('h4','',translation.title),this.#node('p','ag-cr__body',translation.body)); details.append(translated); content.append(details);
      }
      if (review.incentivized) content.append(this.#node('p','ag-cr__source','An incentive was offered regardless of rating.'));
      if (Array.isArray(review.media) && review.media.length) {
        const photos = this.#node('div','ag-cr__photos');
        for (const media of review.media) {
          if (!['image','video'].includes(media.kind)) continue;
          const isVideo = media.kind === 'video', url = isVideo ? this.#videoUrl(media.url) : this.#imageUrl(media.url); if (!url) continue;
          const poster = isVideo ? this.#imageUrl(media.posterUrl) : url;
          const link = this.#node('a'); link.href = url; link.dataset.agCrPhoto = ''; link.dataset.agCrMediaKind = media.kind; link.dataset.agCrPoster = poster; link.setAttribute('aria-label',`${isVideo ? 'Play video' : 'View photo'} by ${review.authorName}`);
          const img = this.#node('img'); if (poster) img.src = poster; img.width = media.width || 104; img.height = media.height || 104; img.loading = 'lazy'; img.alt = media.alt || (isVideo ? 'Customer video' : 'Customer photo');
          const sources = new Map();
          for (const variant of Array.isArray(media.variants) ? media.variants : []) {
            const source = this.#imageUrl(variant.url);
            if (source && Number.isInteger(variant.width) && variant.width > 0) sources.set(variant.width, source);
          }
          if (sources.size) img.srcset = [...sources].sort(([a],[b])=>a-b).map(([width,source])=>`${source} ${width}w`).join(', ');
          img.sizes = '(max-width: 600px) 132px, 168px'; link.append(poster ? img : this.#node('span','ag-cr__video-fallback','Customer video')); if (isVideo) { const badge = this.#node('span','ag-cr__video-badge','▶ Video'); badge.setAttribute('aria-hidden','true'); link.append(badge); } photos.append(link);
        }
        content.append(photos);
      }
      if (review.reply) { const reply = this.#node('div','ag-cr__reply'); reply.append(this.#node('strong','',`${review.reply.authorName} replied`),this.#node('p','',review.reply.body)); content.append(reply); }
      const source = this.#node('span','ag-cr__source',`${review.source === 'native' ? 'Collected by this store' : 'Source: '+(review.sourceLabel || review.source)} · `);
      const count = this.#node('span','',review.helpful); count.dataset.agCrHelpfulCount = ''; source.append(count, document.createTextNode(' found this helpful')); content.append(source);
      article.append(author,content); this.#actions(article); return article;
    }
    async #loadCompact(signal) {
      const results = this.querySelector('[data-ag-cr-results]');
      if (!results) return;
      try {
        const fetchReviews = async productId => {
          const query = new URLSearchParams({placement:'widget', productId, minRating:'4', sort:'newest', pageSize:'6'});
          const response = await readReviewResponse(this, `${this.dataset.api.replace(/\/$/,'')}/list?${query}`, {signal});
          if (!response.ok) throw new Error('Reviews unavailable');
          const data = await response.json();
          if (!Array.isArray(data.reviews)) throw new Error('Invalid reviews');
          return data.reviews.filter(review => review.rating >= 4);
        };
        let reviews = await fetchReviews(this.dataset.product || '');
        if (this.dataset.product && reviews.length < 6) {
          const seen = new Set(reviews.map(review => review.id));
          const extra = await fetchReviews('');
          reviews = [...reviews, ...extra.filter(review => !seen.has(review.id) && seen.add(review.id))];
        }
        if (signal.aborted) return;
        const summary = results.querySelector('[data-ag-cr-all-card]');
        results.replaceChildren(...reviews.slice(0,6).map(review => this.#review(review)));
        if (!reviews.length) results.append(this.#node('p','ag-cr__empty','No 4–5 star reviews yet. Explore all ratings.'));
        if (summary) results.append(summary);
        this.#railRefresh?.();
      } catch { /* Keep the filtered Liquid snapshot available. */ }
    }
    #appendMedia(grid, reviews) {
      const fragment = document.createDocumentFragment();
      for (const review of reviews) {
        if (this.#overviewReviews.has(review.id)) continue;
          const article = this.#review(review);
          this.#overviewReviews.set(review.id,article);
          const photo = article.querySelector('.ag-cr__photos a')?.cloneNode(true);
          if (!photo) continue;
          const figure = this.#node('figure','ag-cr__media-tile');
          photo.dataset.agCrOpenReview = review.id;
          photo.dataset.agCrCaption = `${review.title || 'Customer experience'} — ${review.authorName}`;
          const img = photo.querySelector('img');
          if (img) { img.sizes = '(max-width: 600px) 42vw, (max-width: 1000px) 28vw, 200px'; img.loading = this.dataset.layout === 'compact' ? 'lazy' : 'eager'; }
          const caption = this.#node('figcaption');
          caption.append(this.#node('span','',review.authorName));
          if (this.dataset.layout === 'compact' && this.dataset.product) caption.append(this.#node('span','ag-cr__context',this.#context(review)));
          if (this.dataset.layout === 'compact') {
            const rating = this.#node('span','ag-cr__tile-rating',`${review.rating}/5 ★`);
            rating.setAttribute('aria-label',`${review.rating} out of 5 stars`); photo.append(rating);
          }
          const permalink = this.#permalink(review.id);
          if (permalink) { const link = this.#node('a','','Read review'); link.href = permalink; link.dataset.agCrOpenReview = review.id; caption.append(link); photo.href = permalink; }
          figure.append(photo,caption); fragment.append(figure);
      }
      grid.append(fragment);
    }
    async #loadCompactMedia(section, grid, signal) {
      section.querySelector('.ag-cr__media-controls')?.remove();
      const headingActions = this.querySelector('.ag-cr__heading-actions');
      headingActions?.querySelector('.ag-cr__rail-controls')?.remove();
      const base = this.dataset.api.replace(/\/$/,'');
      const sources = this.dataset.product ? [this.dataset.product, ''] : [''];
      let source = 0, page = 1, busy = false, failed = false;
      const more = () => source < sources.length;
      const nextPage = async () => {
        if (!more()) return [];
        const query = new URLSearchParams({placement:'widget',media:'with_media',minRating:'4',sort:'newest',pageSize:'12',productId:sources[source],page:String(page)});
        const response = await readReviewResponse(this, `${base}/list?${query}`, {signal});
        if (!response.ok) throw new Error('Photos unavailable');
        const data = await response.json();
        if (!Array.isArray(data.reviews) || !Number.isFinite(data.total)) throw new Error('Invalid photos');
        // Advance only after success, so a failed page can be retried.
        if (page * 12 >= data.total) { source++; page = 1; } else page++;
        return data.reviews.filter(review => review.rating >= 4);
      };
      const controls = this.#node('div','ag-cr__media-controls');
      const status = this.#node('span','ag-cr__media-status'); status.setAttribute('role','status');
      const retry = this.#node('button','ag-cr__button','Try again'); retry.type = 'button';
      const nav = this.#node('nav','ag-cr__rail-controls'); nav.setAttribute('aria-label','Browse customer photos');
      const prev = this.#node('button','ag-cr__button','←'), next = this.#node('button','ag-cr__button','→');
      for (const [button,label] of [[prev,'Previous photos'],[next,'Next photos']]) { button.type = 'button'; button.setAttribute('aria-label',label); }
      nav.hidden = true; controls.hidden = true; retry.hidden = true;
      nav.append(prev,next); headingActions?.append(nav);
      controls.append(status,retry); section.append(controls);
      const refresh = () => {
        const remaining = grid.scrollWidth - grid.clientWidth - grid.scrollLeft;
        prev.disabled = grid.scrollLeft < 2;
        next.disabled = busy || (remaining < 2 && !more());
        nav.hidden = section.hidden;
        controls.hidden = !busy && !failed;
        retry.hidden = !failed; retry.disabled = busy;
        grid.style.setProperty('--ag-cr-media-fade-start',grid.scrollLeft > 2 ? '24px' : '0px');
        grid.style.setProperty('--ag-cr-media-fade-end',remaining > 2 ? '40px' : '0px');
      };
      const loadMore = async () => {
        if (busy || !more() || signal.aborted) return;
        busy = true; failed = false; status.textContent = 'Loading photos…'; refresh();
        const before = grid.childElementCount;
        try {
          // Skip pages containing only already-pinned reviews, with a bounded request batch.
          for (let attempt = 0; attempt < 4 && more() && grid.childElementCount === before; attempt++) {
            const reviews = await nextPage(); if (signal.aborted) return;
            this.#appendMedia(grid,reviews);
          }
        } catch (error) {
          if (!signal.aborted) { failed = true; status.textContent = 'Photos could not load. Please try again.'; }
        } finally {
          busy = false; if (!failed) status.textContent = ''; refresh();
        }
      };
      try {
        let featured = [];
        try {
          const response = await readReviewResponse(this, `${base}/projection`, {signal,cache:'no-store'});
          if (response.ok) { const data = await response.json(); if (Array.isArray(data.featured)) featured = data.featured; }
        } catch { /* Recent photos still work while the pinned projection is unavailable. */ }
        let recent = [];
        try { recent = await nextPage(); } catch { /* Keep pinned stories and let the reader retry. */ }
        if (signal.aborted) return;
        this.#overviewReviews.clear(); grid.replaceChildren();
        this.#appendMedia(grid, selectCompactGallery(recent,featured,this.dataset.product || ''));
        // Fill the first six positions, including when a product has only one photo review.
        for (let attempt = 0; attempt < 4 && !failed && grid.childElementCount < 6 && more(); attempt++) await loadMore();
        if (signal.aborted) return;
        section.hidden = !grid.childElementCount && !more();
        refresh();
        const options = {signal};
        retry.addEventListener('click',()=>void loadMore(),options);
        prev.addEventListener('click',()=>grid.scrollBy({left:-grid.clientWidth * .8,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'}),options);
        next.addEventListener('click',async()=>{
          if (grid.scrollWidth-grid.clientWidth-grid.scrollLeft < 160) await loadMore();
          if (!signal.aborted) grid.scrollBy({left:grid.clientWidth * .8,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
        },options);
        grid.addEventListener('scroll',()=>{
          refresh();
          if (!failed && grid.scrollLeft > 0 && grid.scrollWidth-grid.clientWidth-grid.scrollLeft < 180) void loadMore();
        },{signal,passive:true});
        window.addEventListener('resize',refresh,{signal,passive:true});
      } catch { section.hidden = !grid.childElementCount; }
    }
    async #loadMedia(signal) {
      const section = this.querySelector('[data-ag-cr-media-overview]'), grid = this.querySelector('[data-ag-cr-media-grid]');
      if (!section || !grid) return;
      if (this.dataset.layout === 'compact') return this.#loadCompactMedia(section,grid,signal);
      section.hidden = true;
      try {
        const query = new URLSearchParams({media:'with_media',sort:'newest',pageSize:'6',productId:this.dataset.product || ''});
        const rating = this.querySelector('[name=rating]')?.value || '4plus';
        if (rating === '4plus') query.set('minRating','4'); else query.set('rating',rating);
        const response = await readReviewResponse(this, `${this.dataset.api.replace(/\/$/,'')}/list?${query}`, {signal});
        if (!response.ok) return;
        const data = await response.json();
        if (!Array.isArray(data.reviews) || signal.aborted) return;
        this.#overviewReviews.clear(); grid.replaceChildren(); this.#appendMedia(grid,data.reviews.slice(0,6));
        section.hidden = !grid.childElementCount;
      } catch { /* The ordinary reviews and media filter remain available. */ }
    }
    async #load() {
      if (!this.dataset.api) return;
      this.#abort?.abort(); this.#abort = new AbortController();
      void this.#loadMedia(this.#abort.signal);
      const controller = this.#abort, status = this.querySelector('[data-ag-cr-status]'), results = this.querySelector('[data-ag-cr-results]');
      this.querySelector('[data-ag-cr-prev]').disabled = true; this.querySelector('[data-ag-cr-next]').disabled = true;
      status.textContent = 'Loading reviews…'; delete status.dataset.error; results.setAttribute('aria-busy','true');
      try {
        const query = new URLSearchParams(new FormData(this.querySelector('form'))); query.set('page',String(this.#page)); query.set('pageSize','6'); query.set('productId',this.dataset.product || '');
        if (query.get('rating') === '4plus') { query.set('rating','0'); query.set('minRating','4'); }
        const response = await readReviewResponse(this, `${this.dataset.api.replace(/\/$/,'')}/list?${query}`,{signal:controller.signal});
        if (!response.ok) throw new Error('Reviews could not be loaded. Please retry your filters.');
        const data = await response.json(); if (!Array.isArray(data.reviews) || !Number.isFinite(data.total)) throw new Error('Reviews are temporarily unavailable.');
        const fragment = document.createDocumentFragment(); data.reviews.forEach(review => fragment.append(this.#review(review)));
        if (!data.reviews.length) fragment.append(this.#node('p','ag-cr__empty','No reviews match these filters. Try another rating or search.'));
        results.replaceChildren(fragment); this.#total = data.total; this.#renderedPage = this.#page;
        this.#measurement?.refresh();
        this.querySelector('[data-ag-cr-average]').textContent = data.aggregate.average;
        this.querySelector('[data-ag-cr-count]').textContent = data.aggregate.count;
        const overallStars = this.querySelector('.ag-cr__summary .ag-cr__stars'); overallStars.style.setProperty('--ag-cr-rating',data.aggregate.average*20+'%'); overallStars.setAttribute('aria-label',`${data.aggregate.average} out of 5 stars`);
        this.querySelectorAll('[data-ag-cr-rating]').forEach(button => { const rating = Number(button.dataset.agCrRating), count = data.aggregate.distribution[rating-1]; button.lastElementChild.textContent = count; button.querySelector('.ag-cr__bar>span').style.width = (data.aggregate.count ? count/data.aggregate.count*100 : 0)+'%'; button.setAttribute('aria-pressed',String(query.get('rating')===String(rating))); });
        status.textContent = `${data.total} ${data.total === 1 ? 'review matches' : 'reviews match'} your filters.`;
        this.querySelector('[data-ag-cr-page]').textContent = `Page ${this.#page}`;
        this.querySelector('[data-ag-cr-prev]').disabled = this.#page <= 1;
        this.querySelector('[data-ag-cr-next]').disabled = this.#page*6 >= this.#total;
      } catch (error) { if (error.name !== 'AbortError') { this.#page = this.#renderedPage; status.textContent = 'Reviews could not be loaded. Your previous results are still available. Please try again.'; status.dataset.error = ''; this.querySelector('[data-ag-cr-prev]').disabled = this.#page <= 1; this.querySelector('[data-ag-cr-next]').disabled = this.#page*6 >= this.#total; } }
      finally { if (this.#abort === controller) results.removeAttribute('aria-busy'); }
    }
  });
}
